
import { defineComponent } from "vue";
interface Column {
	_id: string;
	name: string;
	selected: boolean;
	dataType: "int" | "str" | "float";
	statsDataType: "categorical" | "continuous";
	units: string;
}
interface TableData {
	columns: Column[];
	tableData: { [columnName: string]: string }[];
	options: any;
}

export default defineComponent({
	name: "imdex-map-table",
	props: {
		title: {
			type: String,
			default: ""
		},
		value: {
			type: Boolean,
			default: false
		},
		variableMaps: {
			type: Object as () => any,
			default: () => {
				return {};
			}
		},
		tableData: {
			type: Object as () => any,
			default: () => {
				return {
					columns: [] as Column[],
					tableData: [] as { [columnName: string]: string }[],
					options: {}
				} as TableData;
			}
		}
	},
	data: () => {
		return {
			sort: 1,
			sortName: "",
			loading: true,
			page: 1,
			perPage: 10,
			perPageSelector: false,
			tempTableData: {
				columns: [] as Column[],
				tableData: [] as { [columnName: string]: string }[],
				options: {}
			} as TableData
		};
	},
	mounted() {
		this.$nextTick(() => {
			this.tempTableData = { ...this.tableData };
			this.loading = false;
		});
	},
	computed: {
		availableStatsDatatypes() {
			const filteredDataColumns = (this.filteredData.length && this.filteredData[0]) || null;

			const availableStatsDatatypes: any = {};

			if (filteredDataColumns) {
				for (const column in filteredDataColumns) {
					const columnData = this.tableData.columns.find(
						(item: Column) => item.name === column
					) as Column;
					if (
						columnData?.dataType === "int" ||
						columnData?.dataType === "float" ||
						!isNaN(filteredDataColumns[column])
					) {
						availableStatsDatatypes[column] = [
							{
								text: this.$t(`import.mapTable.categorical`),
								value: "categorical"
							},
							{ text: this.$t(`import.mapTable.continuous`), value: "continuous" }
						];
					} else {
						availableStatsDatatypes[column] = [
							{
								text: this.$t(`import.mapTable.categorical`),
								value: "categorical"
							}
						];
					}
				}
				return availableStatsDatatypes;
			} else {
				return null;
			}
		},
		availableUnits() {
			return [
				{ text: this.$t(`import.mapTable.unitsFeet`), value: "ft" },
				{ text: this.$t(`import.mapTable.unitsMeter`), value: "m" },
				{ text: this.$t(`import.mapTable.unitsDegrees`), value: "deg" },
				{ text: this.$t(`import.mapTable.unitsNone`), value: "none" }
			];
		},
		mappedColumns() {
			return (
				Object.keys(this.variableMaps).map((item: string) => {
					return this.variableMaps[item];
				}) || []
			);
		},
		columnsSelected() {
			return this.tempTableData.columns
				.filter((item: any) => {
					return item.selected;
				})
				.map((item: any) => {
					return this.tempTableData.columns
						.map((item: any) => {
							return item._id;
						})
						.indexOf(item._id);
				});
		},
		options() {
			return this.tableData.options;
		},
		hasNextPage() {
			return this.tableData.tableData.length > this.perPage * this.page;
		},
		filteredData() {
			const d = this.sort;
			const td = [...this.tableData.tableData];
			if (this.sortName != "") {
				td.sort((a, b) => {
					if (a[this.sortName] > b[this.sortName]) return 1 * d;
					if (a[this.sortName] < b[this.sortName]) return -1 * d;
					return 0;
				});
			}
			return td.slice(this.perPage * (this.page - 1), this.perPage * this.page);
		}
	},
	methods: {
		checkIsSortable(column: any) {
			if (this.options == null) return true;
			if (
				this.options.columns != null &&
				Object.keys(this.options.columns).indexOf(column) > -1
			) {
				if (this.options.columns[column].sortable != null) {
					return this.options.columns[column].sortable;
				}
			} else {
				return true;
			}
		},
		getStyleFromOptions(column: any) {
			if (this.options == null) return "";
			let style = ``;
			if (
				this.options.columns != null &&
				Object.keys(this.options.columns).indexOf(column) > -1
			) {
				if (this.options.columns[column].maxWidth != null) {
					style = style.concat(`width:${this.options.columns[column].maxWidth}`);
				}
			}
			return style;
		},
		getRowStyleFromOptions() {
			if (this.options == null) return ``;
			let style = ``;
			if (
				this.options.rows != null &&
				Object.keys(this.options.rows).indexOf("spacing") > -1
			) {
				if (this.options.rows.spacing != null) {
					style = style.concat(`padding:${this.options.rows.spacing} 8px;`);
				}
			}
			return style;
		},
		formatHeader(header: string) {
			return header.replaceAll("_", " ");
		},
		prevPage() {
			this.page = this.page - 1;
		},
		async nextPage() {
			this.page = this.page + 1;
			const query: any = this.$route.query;
			query.page = this.page;
			query.limit = this.perPage;
			await this.$router.push({ query });
		},
		sortBy(column: string) {
			if (this.sortName == column) {
				this.sort = this.sort * -1;
			}
			this.sortName = column;
		},
		isVisible(name: string) {
			const slotlist = Object.keys({ ...this.$slots });
			return slotlist.filter((item) => item == name).length > 0;
		}
	},
	watch: {
		tableData: {
			deep: true,
			handler(v) {
				this.tempTableData = { ...v };
			}
		},
		tempTableData: {
			deep: true,
			handler(v) {
				this.$emit("update", v);
			}
		}
	}
});
